import React, { useState, useReducer, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import { strings } from '../locate';
import { config, resolveNotificationMessage } from '../helpers/constants';
import Service from "../services/service";
import { ReactComponent as UsersIcon } from '../assets/icons/team.svg';
import { ReactComponent as CertificateIcon } from '../assets/icons/certificate.svg';
import { ReactComponent as TeacherIcon } from '../assets/icons/teacher.svg';
import { ReactComponent as GiftIcon } from '../assets/icons/gift.svg';
import { ReactComponent as SepaIcon } from '../assets/icons/sepa.svg';
import { ReactComponent as SofortIcon } from '../assets/icons/sofort.svg';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { Link, Tooltip, Switch, FormGroup, Select, FormControlLabel, FormHelperText, Paper, Grid, Box, Typography, InputLabel, FormControl, Button, Tab, Tabs, AppBar, Dialog, List, DialogActions, DialogContent, DialogTitle, ListItem, TextField, MenuItem } from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';
import IconLabel from './IconLabel';
import LoadingOverlayButton from './LoadingOverlayButton';


export default function CheckoutLoginPanel(props) {
  const useStyles = makeStyles((theme) => ({
    root: {
      display: "grid",  // https://grid.layoutit.com/?id=54hwQfb
      gridTemplateColumns: "10px 1fr 1px 1fr 10px",
      gridTemplateRows: (props.wizard) ? "20px min-content min-content 0px" : "80px min-content min-content min-content",
      gridTemplateAreas: `
          "leftMargin infoBar infoBar infoBar rightMargin"
          "leftMargin loginPanel centerBar registerPanel rightMargin"
          "leftMargin licensePanel centerBar registerPanel rightMargin"
          "leftMargin bottomBar bottomBar bottomBar rightMargin"
          `,
      gap: "8px 20px",
    },
    leftMargin: { gridArea: "leftMargin" },
    rightMargin: { gridArea: "rightMargin" },
    infoBar: {
      gridArea: "infoBar",
      alignSelf: "center",
      justifySelf: "center",
      display: (props.wizard) ? "none" : "block"
    },
    bottomBar: {
      paddingTop: 32,
      gridArea: "bottomBar",
      alignSelf: "center",
      justifySelf: "center",
      display: (props.wizard) ? "none" : "block"
    },
    loginPanel: {
      gridArea: "loginPanel"
    },
    registerPanel: {
      gridArea: "registerPanel"
    },
    licensePanel: {
      gridArea: "licensePanel"
    },
    centerBar: {
      gridArea: "centerBar",
      borderLeft: "1px solid #0D4A81",
    },
    fullWidth: {
      width: "100%",
      marginBottom: theme.spacing(1)
    },
    rightButton: {
      width: "100%",
      textAlign: "right"
    }
  }));

  const state = useContext(AppContext);
  const dispatch = useContext(DispatchContext);
  const classes = useStyles();
  const s = state.strings;
  const { onLoggedIn, onRegistered } = props;

  const [userDetail, setUserDetail] = React.useState({
    regCode: '',
    regMailSent: false
  });
  const [sendCodeDelay, setSendCodeDelay] = React.useState(30);
  const [sendAttempts, setSendAttempts] = React.useState(0);

  useEffect(() => {
    const id = setInterval(sendTimer, 1000);
    return () => { clearInterval(id) };
  }, [sendCodeDelay]);


  const sendTimer = () => {
    setSendCodeDelay(Math.max(0, sendCodeDelay - 1));
  }


  const handleFormChange = (newValue, key) => {
    dispatch(actions.setCheckoutDetails({ [key]: newValue }));
  }

  const [loginLoading, setLoginLoading] = React.useState(false);
  const loginLoadingRef = React.useRef(false);
  const setLoginLoadingSync = (v) => { loginLoadingRef.current = v; setLoginLoading(v); };

  const [registerLoading, setRegisterLoading] = React.useState(false);
  const registerLoadingRef = React.useRef(false);
  const setRegisterLoadingSync = (v) => { registerLoadingRef.current = v; setRegisterLoading(v); };

  const [sendCodeLoading, setSendCodeLoading] = React.useState(false);
  const sendCodeLoadingRef = React.useRef(false);
  const setSendCodeLoadingSync = (v) => { sendCodeLoadingRef.current = v; setSendCodeLoading(v); };

  const handleLogin = () => {
    if (loginLoadingRef.current) return;
    setLoginLoadingSync(true);
    Service.login(state.checkout.loginMail, state.checkout.loginPassword).then(res => {
      setLoginLoadingSync(false);
      if (res.success) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          if (onLoggedIn) {
            onLoggedIn();
          } else {
            // only free package ordered, do nothing but close the dialog
            if (state.checkout.productId == 30110) {
              dispatch(actions.openPaymentDialog(false));
            } else {
              dispatch(actions.setCheckoutDetails({ paymentStep: state.checkout.paymentStep + 1 }));
            }
            dispatch(actions.openToast({ type: "success", message: s.notificationMessage.loggedIn.format(getInfoRes.data.mail), status: true }));
          }
        });
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  const doRegister = (code) => {
    Service.registerUser(state.checkout.registerMail, state.checkout.registerPassword, state.checkout.registerLicense, code || userDetail.regCode).then(res => {
      setRegisterLoadingSync(false);
      if (res.success && res.data && res.data.UserId) {
        Service.getUserInfo().then(getInfoRes => {
          dispatch(actions.setUserDetails(getInfoRes.data));
          if (onRegistered) {
            onRegistered(getInfoRes.data.mail);
          } else {
            // If only free credits selected, close payment dialog now, because credits have been added to new account already. 
            // For real product, continue checkout
            if (state.checkout.productId == 30110) {
              dispatch(actions.openPaymentDialog(false));
            } else {
              dispatch(actions.setCheckoutDetails({ paymentStep: state.checkout.paymentStep + 1 }));
            }
            dispatch(actions.openToast({ type: "success", message: s.notificationMessage.accountCreated, status: true }));
          }
        });
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  const doSendActivationCode = () => {
    Service.registerUser(state.checkout.registerMail, state.checkout.registerPassword, state.checkout.registerLicense, null).then(res => {
      setSendCodeLoadingSync(false);
      if (res.success) {
        if (res.data && res.data.code) {
          doRegister(res.data.code);
        } else {
          dispatch(actions.openToast({ type: "info", message: resolveNotificationMessage(res.message, s.notificationMessage), status: true }));
          setUserDetail({ ...userDetail, regCode: "", regMailSent: true });
          setSendCodeDelay(30);
          setSendAttempts(sendAttempts + 1);
        }
      } else {
        dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
      }
    });
  }

  const handleRegister = () => {
    if (registerLoadingRef.current) return;
    setRegisterLoadingSync(true);
    captchaAction("register", doRegister, () => setRegisterLoadingSync(false));
  }

  const handleSendActivationCode = () => {
    if (sendCodeLoadingRef.current) return;
    setSendCodeLoadingSync(true);
    captchaAction("sendactivationcode", doSendActivationCode, () => setSendCodeLoadingSync(false));
  }

  const captchaAction = (action, fn, onFail) => {
    const captchas = document.getElementsByClassName("grecaptcha-badge");
    if (captchas.length > 0 && captchas[0].style.visibility != "hidden") {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.captchaKey, { action: action }).then(function (token) {
          Service.verifyReCaptcha(token).then((verifyRes) => {
            if (verifyRes.success) {
              fn();
            } else {
              if (onFail) onFail();
              dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(verifyRes.errors[0], s.notificationMessage) }));
            }
          });
        });
      });
    } else {
      fn();
    }
  }

  const handleEditMailAddress = () => {
    setUserDetail({ ...userDetail, regCode: "", regMailSent: false });
  }

  const handleResendCode = () => {
    if (sendCodeLoadingRef.current) return;
    setSendCodeLoadingSync(true);
    doSendActivationCode();
  }

  const handleChange = (value, key) => {
    setUserDetail({
      ...userDetail,
      [key]: value
    })
  }




  return (
    <div className={classes.root}>
      <div className={classes.leftMargin} />
      <div className={classes.rightMargin} />
      <div className={classes.infoBar}>
        <IconLabel icon={<InfoOutlinedIcon />} label={s.loginRequired} />
      </div>
      <div className={classes.centerBar}>
      </div>
      <div className={classes.registerPanel}>
        {userDetail.regMailSent ?
          <>
            <Typography variant="body2" color="secondary" >{s.loginDialog.activationSent.format(state.checkout.registerMail)}</Typography>
            <form>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label={s.loginDialog.activationCode}
                autoFocus
                value={userDetail.regCode}
                onChange={(evt) => handleChange(evt.target.value, 'regCode')} /></form>
            <Grid container direction="row" justifyContent="center">
              <Grid item xs>
                {(sendAttempts > 2 && sendCodeDelay > 0) ? <Typography variant="body2">{s.loginDialog.resendCode} (in {sendCodeDelay})</Typography> :
                  <Link href="#" variant="body2" onClick={() => handleResendCode()}>
                    {s.loginDialog.resendCode}
                  </Link>}
              </Grid>
              <Grid item>
                <Link href="#" variant="body2" onClick={() => handleEditMailAddress()}>
                  {s.loginDialog.back}
                </Link>
              </Grid>
            </Grid>
            <LoadingOverlayButton
              type="submit"
              style={{ marginTop: 16 }}
              fullWidth
              variant="contained"
              color="primary"
              loading={registerLoading}
              disabled={userDetail.regCode.length != 4}
              onClick={handleRegister}>
              {s.loginDialog.activateAccount}
            </LoadingOverlayButton>
          </>
          : <>
            <Typography color="primary" style={{ marginTop: "8px", marginBottom: "8px" }}>{s.paymentDialog.signUpHeading}</Typography>
            <TextField className={classes.fullWidth} size="small" required label={s.common.mailAddress}
              value={state.checkout.registerMail}
              onChange={(e) => handleFormChange(e.target.value, "registerMail")}
            />
            <TextField size="small" className={classes.fullWidth} required label={s.common.password} type="password"
              value={state.checkout.registerPassword}
              onChange={(e) => handleFormChange(e.target.value, "registerPassword")}
            />
            <FormHelperText>{s.loginDialog.passwordRule}</FormHelperText>
            {!props.wizard && <>
              <Typography style={{ marginTop: "8px", marginBottom: "8px" }} color="primary">{s.loginDialog.alreadyUsingPlagAware}</Typography>
              <TextField className={classes.fullWidth} size="small" label={s.common.licenseCode}
                value={state.checkout.loginLicense}
                onChange={(e) => handleFormChange(e.target.value, "registerLicense")}
              />
              <FormHelperText>{s.userDialog.enterLicense}</FormHelperText>
            </>}
            <div className={classes.rightButton}>
              <LoadingOverlayButton variant="contained" color="primary" loading={sendCodeLoading} disabled={!state.checkout.registerMail || !state.checkout.registerPassword} onClick={handleSendActivationCode}>
                {s.loginDialog.register}
              </LoadingOverlayButton>
            </div>
          </>}
      </div>
      <div className={classes.bottomBar}>
        <Typography variant="body2" color="secondary">{s.loginDialog.acceptTerms}</Typography>
      </div>
      <div className={classes.loginPanel}>
        <Typography color="primary" style={{ marginTop: "8px", marginBottom: "8px" }}>{s.paymentDialog.logInNow}</Typography>
        <TextField className={classes.fullWidth} size="small" required label={s.common.mailAddress}
          value={state.checkout.loginMail}
          onChange={(e) => handleFormChange(e.target.value, "loginMail")}
        />
        <TextField type="password" className={classes.fullWidth} size="small" required label={s.common.password}
          value={state.checkout.loginPassword}
          onChange={(e) => handleFormChange(e.target.value, "loginPassword")}
        />
        <div className={classes.rightButton}>
          <LoadingOverlayButton variant="contained" color="primary" onClick={handleLogin} loading={loginLoading} disabled={!state.checkout.loginMail || !state.checkout.loginPassword}>
            {s.loginDialog.login}
          </LoadingOverlayButton>
        </div>
      </div>
    </div>
  );
}
